import { Extension } from '@tiptap/core'
import { Plugin, PluginKey, EditorState } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { CellSelection } from '@tiptap/pm/tables'

const ACTIVE_CELL_CLASS = 'active-cell'

type PluginState = {
  decorations: DecorationSet
  mouseDownInCell: boolean
}

const pluginKey = new PluginKey<PluginState>('activeCellHighlight')

const buildDecorations = (state: EditorState): DecorationSet => {
  const { selection, doc } = state
  if (selection instanceof CellSelection) return DecorationSet.empty
  const $from = selection.$from
  for (let depth = $from.depth; depth >= 0; depth--) {
    const node = $from.node(depth)
    if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
      const pos = $from.before(depth)
      return DecorationSet.create(doc, [
        Decoration.node(pos, pos + node.nodeSize, { class: ACTIVE_CELL_CLASS }),
      ])
    }
  }
  return DecorationSet.empty
}

export const ActiveCellHighlight = Extension.create({
  name: 'activeCellHighlight',
  addProseMirrorPlugins() {
    return [
      new Plugin<PluginState>({
        key: pluginKey,
        state: {
          init: (_, state): PluginState => ({
            decorations: buildDecorations(state),
            mouseDownInCell: false,
          }),
          apply: (tr, old, _, newState): PluginState => {
            const meta = tr.getMeta(pluginKey) as
              | { mouseDownInCell: boolean }
              | undefined
            const mouseDownInCell = meta?.mouseDownInCell ?? old.mouseDownInCell
            return {
              decorations:
                tr.docChanged || tr.selectionSet
                  ? buildDecorations(newState)
                  : old.decorations,
              mouseDownInCell,
            }
          },
        },
        props: {
          decorations(state) {
            return pluginKey.getState(state)?.decorations ?? DecorationSet.empty
          },
          // ProseMirror が state 更新ごとに view.dom の style を管理するため、
          // attributes prop 経由で caret-color を渡す。inline style の直接操作は
          // updateState で上書きされるため使用しない。
          attributes: (state): { [name: string]: string } => {
            const ps = pluginKey.getState(state)
            const hide =
              state.selection instanceof CellSelection ||
              (ps?.mouseDownInCell ?? false)
            return hide
              ? { style: 'caret-color: transparent', class: 'cell-selecting' }
              : {}
          },
        },
        // capture フェーズで mousedown を捕捉することで、ProseMirror が selection を
        // 更新する dispatch より先に mouseDownInCell: true を送り込み、active-cell の
        // 1フレーム点灯を防ぐ。handleDOMEvents はバブルフェーズのため間に合わない。
        view(editorView) {
          const onMouseDown = (event: MouseEvent) => {
            if (event.button !== 0) return
            const target = event.target instanceof Element ? event.target : null
            if (!target?.closest('td, th')) return
            editorView.dispatch(
              editorView.state.tr.setMeta(pluginKey, { mouseDownInCell: true })
            )
            document.addEventListener(
              'mouseup',
              () => {
                if (!editorView.isDestroyed) {
                  editorView.dispatch(
                    editorView.state.tr.setMeta(pluginKey, {
                      mouseDownInCell: false,
                    })
                  )
                }
              },
              { once: true }
            )
          }
          editorView.dom.addEventListener('mousedown', onMouseDown, {
            capture: true,
          })
          return {
            destroy() {
              editorView.dom.removeEventListener('mousedown', onMouseDown, {
                capture: true,
              })
            },
          }
        },
      }),
    ]
  },
})
